import type { TourRect } from "./geometry.ts"

/**
 * `getBoundingClientRect()` on a `display: contents` element always comes
 * back all-zero — per spec it generates no box of its own — even though
 * everything inside it renders and lays out completely normally. Several
 * real pages tag a `display: contents` wrapper with `data-guide-target`
 * purely to mark a spot in the DOM without disturbing a parent Flex's
 * layout (see `LeadsActivePage.tsx`'s `leads-pipeline-kpis`/`-table`
 * wrappers), which made every tour step targeting them spotlight and
 * position its card at (0,0) instead of over the real content — the exact
 * "card renders off-screen, tour looks stuck" bug this was written to fix.
 * Falling back to the union of the element's children's rects when its own
 * comes back zero fixes this for any current or future target, without
 * requiring every page author to remember not to use `display: contents`.
 */
export const measureElement = (el: Element): TourRect => {
  const r = el.getBoundingClientRect()
  if (r.width > 0 || r.height > 0 || r.top !== 0 || r.left !== 0) {
    return { top: r.top, left: r.left, width: r.width, height: r.height }
  }

  const childRects = Array.from(el.children)
    .map((child) => child.getBoundingClientRect())
    .filter((cr) => cr.width > 0 || cr.height > 0)
  if (childRects.length === 0) {
    return { top: r.top, left: r.left, width: r.width, height: r.height }
  }

  const top = Math.min(...childRects.map((cr) => cr.top))
  const left = Math.min(...childRects.map((cr) => cr.left))
  const right = Math.max(...childRects.map((cr) => cr.right))
  const bottom = Math.max(...childRects.map((cr) => cr.bottom))
  return { top, left, width: right - left, height: bottom - top }
}
